/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonImg,
  IonItemDivider,
  IonLabel,
  IonModal,
  IonPage,
  IonRow,
  IonToolbar,
} from '@ionic/react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ModalClose from '@/assets/icons/close.svg';
import PepperOneLogo from '@/assets/imgs/pepper-one.svg';
import ENVIRONMENT from '@/environments/environment';
import {
  AnalyticsOrigination,
  AnalyticsStepNumber,
} from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { EDNIModalRoutes } from '@/interfaces/EDniModalInterface/EDniModalInterface';
import { registerExitBack, registerExitTry } from '@/services/analytics';
import { fileOpenerService } from '@/services/fileOpener';
import InsightService from '@/services/insights';
import hubInstance from '@/services/websocket';
import { RootState } from '@/store';
import { openBrowser } from '@/utils/appHelper';

import Denial from './Auxiliaries/Denial/Denial';
import Quit from './Auxiliaries/Quit/Quit';
import Timeout from './Auxiliaries/Timeout/Timeout';
import Transition from './Auxiliaries/Transition/Transition';
import UnknownError from './Auxiliaries/UnknownError/UnknownError';
import DniScan from './DniScan/DniScan';
import styles from './EDniModal.module.scss';
import End from './End/End';
import Entry from './entry/Entry';
import ExistingRefinanced from './ExistingRefinanced/ExistingRefinanced';
import LaborData from './LaborData/LaborData';
import OperationShippingFinished from './OperationShipping/OperationShippingFinished/OperationShippingFinished';
import OperationShippingForm from './OperationShipping/OperationShippingForm/OperationShippingForm';
import Payment from './Payment/Payment';
import PersonalData from './PersonalData/PersonalData';
import PersonalFinancesUnavailable from './PersonalFinancesUnavailable/PersonalFinancesUnavailable';
import Signature from './Signature/Signature';

interface EDniModalProps {
  onDismiss: () => void;
}

export default function EDniModal({ onDismiss }: EDniModalProps) {
  const pepperOneFrame = useRef<any>(null);
  const [modalRoute, setModalRoute] = useState<EDNIModalRoutes>(
    EDNIModalRoutes.ENTRY,
  );
  const [flowStep, setFlowStep] = useState<string>('1');
  const [stepTitle, setStepTitle] = useState<string>('Plan de pago');
  const [quitScreen, setQuitScreen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pepperOneUrl, setPepperOneUrl] = useState<string>('');
  const [pepperOneLoaded, setPepperOneLoaded] = useState<boolean>(false);
  const [showBackButton, setShowBackButton] = useState<boolean>(true);
  const [analyticsStep, setAnalyticsStep] = useState<AnalyticsStepNumber>(
    AnalyticsStepNumber.Simulation,
  );

  const creditProposal = useSelector(
    (state: RootState) => state.creditProposal.data,
  );

  const routesWithCloseButton = [
    EDNIModalRoutes.END,
    EDNIModalRoutes.UNKNOWN_ERROR,
    EDNIModalRoutes.DENIAL,
    EDNIModalRoutes.TIMEOUT,
  ];
  const [errorType, setErrorType] = useState<string>('');

  const handleDismiss = () => {
    if (modalRoute === EDNIModalRoutes.OPERATION_SHIPPING_FINISHED) {
      hubInstance.disconnect();
    }
    onDismiss();
  };

  useEffect(() => {
    InsightService.logTrace('superExpressPage', [{ page: modalRoute }]);
    switch (modalRoute) {
      case EDNIModalRoutes.ENTRY:
        setAnalyticsStep(AnalyticsStepNumber.Simulation);
        break;
      case EDNIModalRoutes.DNI_SCAN:
        setAnalyticsStep(AnalyticsStepNumber.Simulation);
        break;
      case EDNIModalRoutes.PERSONAL_DATA:
        setAnalyticsStep(AnalyticsStepNumber.PersonalData);
        break;
      case EDNIModalRoutes.LABOR_DATA:
        setAnalyticsStep(AnalyticsStepNumber.LaborData);
        break;
      case EDNIModalRoutes.OPERATION_SHIPPING:
        setAnalyticsStep(AnalyticsStepNumber.Shipping);
        break;
      case EDNIModalRoutes.TPV:
        setAnalyticsStep(AnalyticsStepNumber.Payment);
        break;
      case EDNIModalRoutes.SIGNATURE:
        setAnalyticsStep(AnalyticsStepNumber.Signature);
        break;
    }
  }, [modalRoute]);

  const selectRoute = (page?: EDNIModalRoutes) => {
    switch (page) {
      case EDNIModalRoutes.ENTRY:
        return (
          <Entry
            nextStep={(route: EDNIModalRoutes) => {
              setModalRoute(route);
              setFlowStep('2');
            }}
            handleLoading={(e: boolean) => setIsLoading(e)}
          />
        );
      case EDNIModalRoutes.DNI_SCAN:
        return (
          <DniScan
            handleLoading={(e: boolean) => setIsLoading(e)}
            nextStep={(route: EDNIModalRoutes) => setModalRoute(route)}
            sendIFrameUrl={(url: string) => setPepperOneUrl(url)}
          />
        );

      case EDNIModalRoutes.PERSONAL_DATA:
        return (
          <PersonalData
            nextStep={(route: EDNIModalRoutes) => setModalRoute(route)}
            handleLoading={(event: boolean) => {
              setIsLoading(event);
            }}
            errorType={(type: string) => setErrorType(type)}
            sendIFrameUrl={(url: string) => setPepperOneUrl(url)}
          />
        );

      case EDNIModalRoutes.OPERATION_SHIPPING:
        return (
          <OperationShippingForm
            nextStep={(route: EDNIModalRoutes) => setModalRoute(route)}
            sendIFrameUrl={(url: string) => setPepperOneUrl(url)}
            handleLoading={(e: boolean) => setIsLoading(e)}
          />
        );
      case EDNIModalRoutes.OPERATION_SHIPPING_FINISHED:
        return <OperationShippingFinished handleClose={handleDismiss} />;
      case EDNIModalRoutes.LABOR_DATA:
        return (
          <LaborData
            nextStep={(route: EDNIModalRoutes) => setModalRoute(route)}
            handleLoading={(event: boolean) => {
              setIsLoading(event);
            }}
            errorType={(type: string) => setErrorType(type)}
            sendIFrameUrl={(url: string) => setPepperOneUrl(url)}
          />
        );

      case EDNIModalRoutes.SIGNATURE:
        return (
          <Signature
            nextStep={(route: EDNIModalRoutes) => {
              setModalRoute(route);
            }}
            handleLoading={(event: boolean) => {
              setIsLoading(event);
            }}
          />
        );
      case EDNIModalRoutes.TPV:
        return (
          <Payment
            nextStep={(route: EDNIModalRoutes) => {
              setModalRoute(route);
              setFlowStep('3');
            }}
            handleLoading={(event: boolean) => {
              setIsLoading(event);
            }}
          />
        );
      case EDNIModalRoutes.END:
        return <End handleClose={handleDismiss} />;
      case EDNIModalRoutes.UNKNOWN_ERROR:
        return (
          <UnknownError step={analyticsStep} handleClose={handleDismiss} />
        );
      case EDNIModalRoutes.DENIAL:
        return <Denial handleClose={handleDismiss} />;
      case EDNIModalRoutes.TIMEOUT:
        return <Timeout handleClose={handleDismiss} />;
      case EDNIModalRoutes.PERSONAL_FINANCES_UNAVAILABLE:
        return (
          <PersonalFinancesUnavailable
            handleClose={handleDismiss}
            errorType={errorType}
          />
        );
      case EDNIModalRoutes.EXISTING_RFN:
        return <ExistingRefinanced handleClose={handleDismiss} />;
      default:
        break;
    }
  };

  const sendCommercialOffers = () => {
    const pepperOneIframe = document.getElementById(
      'pepper-one-super-express',
    ) as HTMLIFrameElement;
    if (pepperOneIframe?.contentWindow) {
      const commercialOffersStored = sessionStorage.getItem(
        'pepperSimulationCommercialOffers',
      );
      commercialOffersStored
        && pepperOneIframe.contentWindow.postMessage(
          {
            simulationCommercialOffers: JSON.parse(commercialOffersStored),
          },
          '*',
        );
    }
  };

  useEffect(() => {
    if (flowStep) {
      switch (flowStep) {
        case '1':
          setStepTitle('Plan de pago');
          break;
        case '2':
          setStepTitle('Contratación');
          break;
        case '3':
          setStepTitle('Firma electrónica');
          break;
        default:
          break;
      }
    }
  }, [flowStep]);

  /*   const listener = (event: any) => {
      if (
        event.origin === ENVIRONMENT.URL_PEPPERONE
        && event.data?.message === 'close CEX'
      ) {
        window.removeEventListener('message', listener);
        handleDismiss();
      } else if (
        event.data?.message === 'Pepper - Signature Contract'
      ) {
        fileOpenerService.openContract(event.data.contractString);
      }
    };

    useEffect(
      () =>
      // window.addEventListener('message', listener, false);

        () => {
          window.removeEventListener('message', listener);
        },
      [],
    ); */

  return (
    <div
      // eslint-disable-next-line react/jsx-max-props-per-line
      className={styles.pepperEdniModal}
      id="pepper-edni-modal"
    >
      {!pepperOneLoaded && (
        <>
          <IonItemDivider className={styles.pepperModalHeaderDivider}>
            <IonHeader className={styles.pepperModalHeader}>
              <IonGrid>
                <IonRow>
                  <IonCol />
                  <IonCol>
                    <IonImg
                      src={PepperOneLogo}
                      className={styles.pepperHeaderLogo}
                    />
                  </IonCol>

                  <IonCol className={styles.headerCloseWrapper}>
                    {showBackButton
                      && !routesWithCloseButton.includes(modalRoute) && (
                        <IonImg
                          src={ModalClose}
                          className={styles.headerCloseBtn}
                          onClick={() => {
                            registerExitTry(
                              {
                                origin: AnalyticsOrigination.App,
                                uuid: creditProposal?.credit_proposal_id,
                              },
                              analyticsStep.toString(),
                            );
                            setQuitScreen(true);
                            setShowBackButton(false);
                          }}
                        />
                    )}
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonHeader>
          </IonItemDivider>
          <div className={styles.pepperModalContent}>
            {isLoading && <Transition />}
            <div className={styles.pepperModalBody}>
              {quitScreen ? (
                <Quit
                  handleReturn={() => {
                    registerExitBack(
                      {
                        origin: AnalyticsOrigination.App,
                        uuid: creditProposal?.credit_proposal_id,
                      },
                      analyticsStep.toString(),
                    );
                    setQuitScreen(false);
                    setShowBackButton(true);
                  }}
                  handleClose={handleDismiss}
                />
              ) : (
                <>
                  {modalRoute !== EDNIModalRoutes.UNKNOWN_ERROR
                    && modalRoute !== EDNIModalRoutes.DENIAL
                    && modalRoute !== EDNIModalRoutes.TIMEOUT
                    && modalRoute !== EDNIModalRoutes.END
                    && modalRoute
                      !== EDNIModalRoutes.PERSONAL_FINANCES_UNAVAILABLE
                    && modalRoute !== EDNIModalRoutes.EXISTING_RFN && (
                      <div className={styles.modalFlowStep}>
                        <IonLabel className={styles.flowStepTitle}>
                          {stepTitle}
                        </IonLabel>
                        <IonRow className={styles.stepsGroup}>
                          <div
                            className={`${
                              flowStep === '1'
                                ? styles.currentStepCircle
                                : styles.stepCircle
                            }`}
                          >
                            1
                          </div>
                          <div
                            className={`${
                              flowStep === '2'
                                ? styles.currentStepCircle
                                : styles.stepCircle
                            }`}
                          >
                            2
                          </div>
                          <div
                            className={`${
                              flowStep === '3'
                                ? styles.currentStepCircle
                                : styles.stepCircle
                            }`}
                          >
                            3
                          </div>
                        </IonRow>
                      </div>
                  )}
                  {selectRoute(modalRoute)}
                </>
              )}
            </div>
          </div>
        </>
      )}
      {pepperOneUrl && (
        <iframe
          // ref={pepperOneFrame}
          src={pepperOneUrl}
          className={styles.pepperIframe}
          title="pepperone"
          allow="microphone; camera; midi; encrypted-media;"
          width="100%"
          height="100%"
          id="pepper-one-super-express"
          onLoad={() => {
            setIsLoading(false);
            setPepperOneLoaded(true);
            sendCommercialOffers();
          }}
        />
      )}
    </div>
  );
}
